import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { formatCsatRatingLabel, CsatDisplayType } from '../../utils/supportMessageCsat';
import {
  publicCsatService,
  PublicCsatSurvey,
} from '../../services/publicCsatService';

type PageState = 'loading' | 'ready' | 'completed' | 'expired' | 'notFound' | 'error';

const RATINGS = [1, 2, 3, 4, 5];

const RATING_LABELS: Record<number, string> = {
  1: 'Muito insatisfeito',
  2: 'Insatisfeito',
  3: 'Neutro',
  4: 'Satisfeito',
  5: 'Muito satisfeito',
};

const RATING_EMOJIS: Record<number, string> = {
  1: '😠',
  2: '😕',
  3: '😐',
  4: '🙂',
  5: '😄',
};

function normalizeDisplayType(value: string | undefined): CsatDisplayType {
  if (value === 'star' || value === 'number' || value === 'emoji')
  {
    return value;
  }

  return 'emoji';
}

export const PublicCsatPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [survey, setSurvey] = useState<PublicCsatSurvey | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!uuid)
    {
      setPageState('notFound');
      return;
    }

    let cancelled = false;
    void (async () => {
      try
      {
        const data = await publicCsatService.getSurvey(uuid);
        if (cancelled)
        {
          return;
        }

        setSurvey(data);
        if (data.status === 'completed')
        {
          setRating(data.submittedRating ?? null);
          setFeedback(data.submittedFeedback ?? '');
          setPageState('completed');
          return;
        }

        if (data.status === 'expired')
        {
          setPageState('expired');
          return;
        }

        setPageState('ready');
        void publicCsatService.markOpened(uuid).catch(() => undefined);
      }
      catch (error)
      {
        if (cancelled)
        {
          return;
        }

        const status = (error as { response?: { status?: number } })?.response?.status;
        setPageState(status === 404 ? 'notFound' : 'error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uuid]);

  const handleSubmit = async () => {
    if (!uuid || rating == null)
    {
      setErrorMessage('Escolha uma nota para continuar.');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    try
    {
      const result = await publicCsatService.submit(uuid, {
        rating,
        feedback: feedback.trim() ? feedback.trim() : undefined,
      });
      setSurvey(current => current
        ? {
            ...current,
            status: 'completed',
            submittedRating: result.rating,
            submittedFeedback: result.feedback,
          }
        : current);
      setRating(result.rating);
      setFeedback(result.feedback ?? '');
      setPageState('completed');
    }
    catch (error)
    {
      const apiMessage = (
        error as { response?: { data?: { message?: string; error?: string; title?: string } } }
      )?.response?.data;
      setErrorMessage(
        apiMessage?.message || apiMessage?.error || apiMessage?.title || 'Não foi possível enviar a avaliação.'
      );
    }
    finally
    {
      setSubmitting(false);
    }
  };

  const displayType = normalizeDisplayType(survey?.displayType);
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-sm border border-slate-200 p-6">
        {pageState === 'loading' && (
          <p className="text-sm text-slate-500">Carregando pesquisa...</p>
        )}
        
        {pageState === 'notFound' && (
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Pesquisa indisponível</h1>
            <p className="mt-2 text-sm text-slate-600">
              Este link de avaliação não é válido ou a pesquisa foi cancelada.
            </p>
          </div>
        )}
        
        {pageState === 'expired' && (
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Prazo encerrado</h1>
            <p className="mt-2 text-sm text-slate-600">
              O prazo de 14 dias para responder esta pesquisa expirou.
            </p>
          </div>
        )}
        
        {pageState === 'error' && (
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Não foi possível abrir a pesquisa</h1>
            <p className="mt-2 text-sm text-slate-600">Tente novamente em alguns instantes.</p>
          </div>
        )}
        
        {(pageState === 'ready' || pageState === 'completed') && survey && (
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Pesquisa de satisfação</p>
            <h1 className="text-lg font-semibold text-slate-900">{survey.message}</h1>
            
            {pageState === 'completed' ? (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-slate-600">Obrigado pela sua avaliação.</p>
                {displayType === 'star' && (
                  <div className="flex gap-1">
                    {RATINGS.map(i => (
                      <svg
                        key={i}
                        className={`w-8 h-8 ${i <= (survey.submittedRating || rating || 0) ? 'text-yellow-400' : 'text-gray-300'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                )}
                {displayType === 'emoji' && (
                  <div className="text-4xl">
                    {RATING_EMOJIS[survey.submittedRating || rating || 0]}
                  </div>
                )}
                {displayType === 'number' && (
                  <div className="text-2xl font-bold text-slate-900">
                    {survey.submittedRating || rating}/5
                  </div>
                )}
                {survey.submittedFeedback && (
                  <p className="text-sm text-slate-600 whitespace-pre-wrap mt-3 p-3 bg-slate-50 rounded-lg">
                    {survey.submittedFeedback}
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-5 space-y-5">
                {/* Star Rating */}
                {displayType === 'star' && (
                  <div className="flex flex-col items-center gap-4">
                    <div className="flex gap-2">
                      {RATINGS.map(value => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setRating(value)}
                          onMouseEnter={() => setHoveredRating(value)}
                          onMouseLeave={() => setHoveredRating(null)}
                          className="transition-transform hover:scale-110 focus:outline-none focus:scale-110"
                          aria-label={`${value} ${value === 1 ? 'estrela' : 'estrelas'}`}
                        >
                          <svg
                            className={`w-12 h-12 transition-colors ${
                              value <= (hoveredRating ?? rating ?? 0)
                                ? 'text-yellow-400'
                                : 'text-gray-300'
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </button>
                      ))}
                    </div>
                    {rating && (
                      <p className="text-sm text-slate-600">
                        <span className="font-semibold">{RATING_LABELS[rating]}</span> ({rating}/5)
                      </p>
                    )}
                  </div>
                )}

                {/* Emoji Rating */}
                {displayType === 'emoji' && (
                  <div className="flex justify-between gap-2">
                    {RATINGS.map(value => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setRating(value)}
                        className={`flex-1 min-h-[4rem] rounded-xl border-2 transition-all ${
                          rating === value
                            ? 'border-blue-500 bg-blue-50 scale-105'
                            : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                        }`}
                        aria-label={`${RATING_LABELS[value]} - ${value}/5`}
                      >
                        <div className="text-3xl">{RATING_EMOJIS[value]}</div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Number Rating */}
                {displayType === 'number' && (
                  <div className="flex justify-between gap-2">
                    {RATINGS.map(value => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setRating(value)}
                        className={`flex-1 min-h-[3.5rem] rounded-xl border-2 text-xl font-bold transition-all ${
                          rating === value
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-slate-200 text-slate-700 hover:border-blue-300'
                        }`}
                        aria-label={`Nota ${value}`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                )}

                {survey.askComment && (
                  <textarea
                    value={feedback}
                    onChange={e => setFeedback(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    placeholder="Comentário opcional"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                )}

                {errorMessage && (
                  <p className="text-sm text-red-600">{errorMessage}</p>
                )}

                <Button
                  onClick={() => void handleSubmit()}
                  disabled={submitting || rating == null}
                  className="w-full"
                >
                  {submitting ? 'Enviando...' : 'Enviar avaliação'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicCsatPage;
